import { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Link } from "react-router-dom";

const STATUS_OPTIONS = ["new", "contacted", "closed"];

const AdminInquiries = () => {
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const load = () => {
    setLoading(true);
    api.get("/admin/inquiries")
      .then(({ data }) => setInquiries(data))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const updateStatus = async (inq, status) => {
    try {
      await api.patch(`/admin/inquiries/${inq.id}`, { status });
      toast.success(`Marked as ${status}`);
      setInquiries((list) => list.map((i) => (i.id === inq.id ? { ...i, status } : i)));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const filtered = filter === "all" ? inquiries : inquiries.filter((i) => i.status === filter);

  return (
    <div>
      <div className="coin-tag mb-2">Buyer Communications</div>
      <h1 className="font-display text-4xl tracking-tight mb-10">Inquiries</h1>

      <div className="flex gap-2 mb-8 border-b border-[#F3EFE0]/10 pb-4">
        {["all", ...STATUS_OPTIONS].map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`coin-tag px-4 py-2 border transition-colors ${
              filter === f
                ? "border-[#D4AF37] text-[#D4AF37]"
                : "border-[#F3EFE0]/15 text-[#A19D94] hover:border-[#F3EFE0]/40"
            }`}
            data-testid={`inquiry-filter-${f}`}
          >
            {f}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-center py-16 text-[#6B6A65]">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="border border-dashed border-[#F3EFE0]/10 p-16 text-center text-[#6B6A65]">
          No inquiries in this view.
        </div>
      ) : (
        <div className="space-y-4" data-testid="inquiry-list">
          {filtered.map((inq) => (
            <div key={inq.id} className="border border-[#F3EFE0]/10 bg-[#0A0A0A] p-6 hover:border-[#D4AF37]/30 transition-colors" data-testid={`inquiry-${inq.id}`}>
              <div className="flex justify-between items-start gap-6 flex-wrap">
                <div className="flex-1 min-w-[300px]">
                  <div className="flex items-center gap-3 mb-2 flex-wrap">
                    <h3 className="font-display text-2xl">{inq.name}</h3>
                    <span className={`coin-tag px-2 py-0.5 border ${
                      inq.status === "new" ? "border-[#D4AF37]/40 text-[#D4AF37]" :
                      inq.status === "contacted" ? "border-[#2B435C]/60 text-[#A19D94]" :
                      "border-[#F3EFE0]/10 text-[#6B6A65]"
                    }`}>{inq.status}</span>
                  </div>
                  <div className="text-sm text-[#A19D94] mb-1">
                    <span className="font-mono">{inq.email}</span>
                    {inq.phone && <span className="text-[#6B6A65]"> · {inq.phone}</span>}
                  </div>
                  <div className="text-sm text-[#6B6A65] mb-3">
                    Re: <Link to={`/coin/${inq.coin_id}`} target="_blank" className="text-[#A19D94] hover:text-[#D4AF37]">{inq.coin_name}</Link>
                    <span className="ml-2 font-mono">{new Date(inq.created_at).toLocaleString()}</span>
                  </div>
                  <p className="text-[#F3EFE0] whitespace-pre-line border-l-2 border-[#D4AF37]/30 pl-4 mt-3">{inq.message}</p>
                </div>
                <div className="flex flex-col gap-2 min-w-[160px]">
                  {STATUS_OPTIONS.map((s) => (
                    <button
                      key={s}
                      onClick={() => updateStatus(inq, s)}
                      disabled={inq.status === s}
                      className={`coin-tag border px-3 py-2 transition-colors ${
                        inq.status === s
                          ? "border-[#D4AF37] text-[#D4AF37] cursor-default"
                          : "border-[#F3EFE0]/15 text-[#A19D94] hover:border-[#D4AF37]/40 hover:text-[#F3EFE0]"
                      }`}
                      data-testid={`inquiry-${inq.id}-status-${s}`}
                    >
                      Mark {s}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminInquiries;
